import { useState } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useToast } from "../ui/toast/ToastProvider";
import { openExternal } from "../../utils/navigation";
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, ArrowRight, ArrowLeft } from 'lucide-react';

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactSupportModal({ isOpen, onClose }: ContactSupportModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [mode, setMode] = useState<'select' | 'write'>('select');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const supportPhoneE164 = '5491131551049';
  const supportText = 'Hola, necesito ayuda con Logrit';
  const waMeUrl = `https://wa.me/${supportPhoneE164}?text=${encodeURIComponent(supportText)}`;

  const openWhatsApp = () => {
    try {
      openExternal(waMeUrl);
    } catch {
      openExternal(waMeUrl);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      showToast({ type: 'error', message: t('support.contact.errors.emptyMessage') });
      return;
    }

    try {
      setSending(true);
      
      await addDoc(collection(db, 'feedback'), {
        email: user?.email || 'Usuario anónimo',
        userId: user?.uid || null,
        message: message.trim(),
        source: 'contact_support',
        status: 'unread',
        createdAt: Timestamp.now()
      });

      showToast({ type: 'success', message: t('support.contact.toasts.sent') });
      setMessage('');
      onClose();
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      showToast({ type: 'error', message: t('support.contact.errors.sendError') });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-[24px] shadow-sm max-h-[80vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: '#fff5eb' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-5 rounded-t-[24px]"
          style={{ backgroundColor: '#055590' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-black text-white">
              {t('support.contact.title')}
            </h2>
          </div>
          <button
            onClick={() => { onClose(); setMode('select'); setMessage(''); }}
            className="w-8 h-8 rounded-2xl hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {mode === 'select' ? (
            /* Selector de opciones */
            <div className="space-y-3">
              {/* Opción WhatsApp */}
              <button
                type="button"
                onClick={openWhatsApp}
                className="w-full p-4 rounded-2xl transition-all shadow-sm flex items-center gap-3"
                style={{ backgroundColor: '#ffffff', opacity: 0.8 }}
              >
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-sm flex-shrink-0"
                  style={{ backgroundColor: '#25D366' }}
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="font-black text-gray-900 dark:text-white truncate">
                    {t('support.contact.whatsapp.title')}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {t('support.contact.whatsapp.description')}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              </button>

              {/* Opción Escribir */}
              <button
                onClick={() => setMode('write')}
                className="w-full p-4 rounded-2xl transition-all shadow-sm flex items-center gap-3"
                style={{ backgroundColor: '#ffffff', opacity: 0.8 }}
              >
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-sm flex-shrink-0"
                  style={{ backgroundColor: '#055590' }}
                >
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="font-black text-gray-900 dark:text-white truncate">
                    {t('support.contact.writeMessage.title')}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {t('support.contact.writeMessage.description')}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              </button>
            </div>
          ) : (
            /* Form para escribir */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  {t('support.contact.form.label')}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('support.contact.form.placeholder')}
                  rows={6}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
                  disabled={sending}
                />
              </div>

              <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex gap-3">
                  <div className="text-2xl">💡</div>
                  <div className="flex-1">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Tip:</strong> Incluye todos los detalles posibles para que podamos ayudarte mejor.
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setMode('select'); setMessage(''); }}
                  className="flex-1 px-4 py-3 rounded-2xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-bold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                  disabled={sending}
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('common.back', { defaultValue: 'Volver' })}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 rounded-2xl font-black shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#ef6e6b', color: '#ffffff' }}
                  disabled={sending || !message.trim()}
                >
                  {sending ? t('support.contact.form.sending') : t('support.contact.form.send')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
